import { useCallback } from "react";
import { useIntl } from "react-intl";
import commonMessages from "../common/messages";

// Backend codes that should surface as a specific, readable message instead
// of the generic "Unknown error" fallback below. RECORD_LOCKED is thrown
// (via HttpsError) by ~40 handlers guarding merge-locked customers/patients/
// appointments (see kudzu-backend common/checkMergeStatus.ts) — every one of
// those call sites gets a readable message for free by handling it here,
// rather than needing a local switch/case at each dialog like
// CHECKOUT_APPOINTMENT_EXISTS/EMAIL_MISMATCH etc. already do in MergeCustomers.
const getKnownErrorMessage = (
  error: unknown,
  formatMessage: ReturnType<typeof useIntl>["formatMessage"],
): string | null => {
  const message = (error as { message?: string } | undefined)?.message;
  if (message === "RECORD_LOCKED") {
    return formatMessage(commonMessages.recordLocked);
  }
  return null;
};

const useDefaultErrorWrapper = () => {
  const { formatMessage } = useIntl();

  const runAsyncFunction = useCallback(
    async <R, A>(func: (args: A) => Promise<R>, args: A): Promise<R> => {
      try {
        return await func(args);
      } catch (error) {
        throw new Error(
          getKnownErrorMessage(error, formatMessage) ??
            formatMessage(commonMessages.defaultError),
        );
      }
    },
    [formatMessage],
  );

  const runSyncFunction = useCallback(
    <R, A>(func: (args: A) => Promise<R>, args: A): Promise<R> => {
      try {
        return func(args);
      } catch (error) {
        throw new Error(
          getKnownErrorMessage(error, formatMessage) ??
            formatMessage(commonMessages.defaultError),
        );
      }
    },
    [formatMessage],
  );

  return { runAsyncFunction, runSyncFunction };
};

export default useDefaultErrorWrapper;
